import React, { useState, useEffect, useRef } from "react";
import { X, Upload, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "@/lib/api";

/**
 * Shared bulk-paste modal for Resend DAC requests.
 *
 * Used by:
 *   - `ResendOnly.jsx` (staff Resend Desk)
 *   - `Admin.jsx`     (admin bulk-paste card)
 *
 * Feb 2026 admin request added FOUR things on top of the original paste-only
 * flow:
 *   1. CSV / Excel file upload (Bharat booking history CSV, xlsx). Rows are
 *      normalised client-side into the same tab-separated text the existing
 *      `/resend/bulk/preview` endpoint already accepts.
 *   2. A "Team member" staff dropdown at the top — pre-fills every row's
 *      staff attribution so admins never have to hand-type it in each row.
 *   3. Duplicate warning per row: the preview response now carries
 *      `already_resent_by` / `already_resent_at` fields when the LPG has
 *      already been resent by anyone (admin/resend_staff/team_staff). The
 *      row shows a red badge like "already resent by ADMIN on 11/08/2026".
 *   4. Ledger untouched — this whole flow only writes to `resend_entries`,
 *      not to `messages` / `lpg_entries`.
 *
 * Bharat-Gas-only constraint is enforced on the SERVER — non-Bharat rows
 * come back inside the `unmatched` bucket with a specific reason.
 */
export function ResendBulkPasteModal({
  onClose,
  onCommitted,
  title = "Resend DAC · Bulk Paste",
  subtitle = "File many requests at once",
}) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [includeFuzzy, setIncludeFuzzy] = useState(false);
  const [staffList, setStaffList] = useState([]); // dropdown source
  const [pickedStaff, setPickedStaff] = useState(""); // one of staffList[i].name
  // Feb 2026 admin request: allow forcing a single-agency route for the
  // WHOLE paste — useful when the admin has a clean LPG list from Ashish
  // without an Agency column and just wants "these 15 IDs to ENGINEER
  // BHARAT GAS". Backend honors `default_agency_group_id` by routing every
  // row to that group irrespective of any parsed agency name.
  const [agencyList, setAgencyList] = useState([]);
  const [pickedAgencyId, setPickedAgencyId] = useState("");
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Load the resend_staff + team_staff users so admins can pre-attribute
    // the whole paste to one person via the dropdown. Uses a lightweight
    // `/resend/staff-list` endpoint that works for both admins and team.
    (async () => {
      try {
        const { data } = await api.get("/resend/staff-list");
        setStaffList(
          (data || []).map((u) => ({
            id: u.id,
            name: u.display_name,
          }))
        );
      } catch {
        setStaffList([]);
      }
    })();
    // Load Bharat / BPCL agencies for the "force single agency" dropdown.
    (async () => {
      try {
        const { data } = await api.get("/resend/agencies");
        // Server response shape: `{ agencies: [{id, name}, …] }` OR a
        // bare array from older builds. Handle both.
        const arr = Array.isArray(data) ? data : data?.agencies || [];
        setAgencyList(arr);
      } catch {
        setAgencyList([]);
      }
    })();
  }, []);

  const onFileChosen = async (file) => {
    if (!file) return;
    setErr("");
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      let lines = [];
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) {
        // Excel — pick the first sheet by default; caller can also paste
        // manually if they want a specific sheet.
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
          raw: false,
        });
        lines = rows.map((r) => r.map((c) => String(c ?? "")).join("\t"));
      } else {
        // Treat everything else as CSV/TSV. The Bharat booking history
        // exports carry an Excel-safe `="123..."` quoting for the consumer
        // number so we strip the leading `=` and surrounding quotes before
        // handing rows to the existing paste parser.
        const decoder = new TextDecoder("utf-8");
        const csvText = decoder.decode(buf);
        lines = _parseCsvLines(csvText);
      }
      // Detect a header row and rebuild every data row as
      //     `<Agency>\t<LPG_ID>\t<DAC>\t<Staff>`
      // which is exactly the format the server-side `_parse_resend_paste`
      // already understands. This keeps the backend contract untouched.
      const rebuilt = _rebuildRowsForParser(lines);
      setText(rebuilt);
      setPreview(null);
      setResult(null);
    } catch (e) {
      setErr("File parse failed: " + (e?.message || String(e)));
    }
  };

  const doPreview = async () => {
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      // Injects a default_staff so the server can attribute rows that have
      // no explicit `Staff` column to the currently-picked dropdown user.
      // Also honours `default_agency_group_id` when the admin has picked a
      // single-agency force override — every parsed row then routes there.
      const { data } = await api.post("/resend/bulk/preview", {
        text,
        default_staff: pickedStaff || undefined,
        default_agency_group_id: pickedAgencyId || undefined,
      });
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!preview) return;
    setBusy(true);
    setErr("");
    try {
      const rows = [
        ...preview.matched.map(stripPreview),
        ...(includeFuzzy ? preview.fuzzy.map(stripPreview) : []),
      ];
      if (!rows.length) {
        setErr("Nothing to submit — no matched rows.");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/resend/bulk/commit", {
        rows,
        force: includeFuzzy,
        default_staff: pickedStaff || undefined,
        default_agency_group_id: pickedAgencyId || undefined,
      });
      setResult(data);
      if ((data.created || []).length > 0) {
        setTimeout(() => onCommitted && onCommitted(), 1200);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      data-testid="resend-bulk-modal"
    >
      <div className="bg-[#F5F5F0] rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-[#DCDCD3] flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#8A6046]">
              {title}
            </div>
            <div className="text-lg font-semibold text-[#1C1C19]">{subtitle}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="resend-bulk-close"
            className="text-[#6B6B61] hover:text-[#1C1C19] p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-auto flex-1">
          {/* Row 1 — team-member picker + agency force + file upload */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
              Team member (attribute all rows)
            </label>
            <select
              value={pickedStaff}
              onChange={(e) => {
                setPickedStaff(e.target.value);
                setPreview(null);
              }}
              className="px-3 py-1.5 border border-[#DCDCD3] rounded text-sm bg-white"
              data-testid="resend-bulk-staff-select"
            >
              <option value="">— none (use per-row staff column) —</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
            <label
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DCDCD3] rounded cursor-pointer hover:bg-white text-sm ml-auto"
              data-testid="resend-bulk-file-label"
              title="CSV / Excel upload — Bharat booking history CSV or aap ki daily sheet"
            >
              <Upload className="w-4 h-4 text-[#385C4B]" />
              <span className="max-w-[200px] truncate">
                {fileName || "Upload CSV / Excel"}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv"
                className="hidden"
                onChange={(e) => onFileChosen(e.target.files?.[0])}
                data-testid="resend-bulk-file-input"
              />
            </label>
          </div>

          {/* Row 2 — Force single-agency route. When set, every parsed row
              goes to this ONE agency regardless of what agency name (if any)
              was in the paste. Perfect for admin-typed lists of raw LPG IDs
              handed off by field staff. */}
          <div className="flex flex-wrap items-center gap-3 -mt-2">
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
              Force single agency (route ALL rows here)
            </label>
            <select
              value={pickedAgencyId}
              onChange={(e) => {
                setPickedAgencyId(e.target.value);
                setPreview(null);
              }}
              className="px-3 py-1.5 border border-[#DCDCD3] rounded text-sm bg-white flex-1 min-w-[240px]"
              data-testid="resend-bulk-agency-select"
            >
              <option value="">
                — none (use agency name per row) —
              </option>
              {agencyList.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            {pickedAgencyId && (
              <span className="text-[10px] font-mono text-emerald-700 whitespace-nowrap">
                ✓ ALL rows will route to this agency
              </span>
            )}
          </div>

          <p className="text-xs text-[#6B6B61]">
            Paste rows OR upload file. Columns:{" "}
            <code className="bg-[#F5F5F0] px-1 rounded">Agency</code>,{" "}
            <code className="bg-[#F5F5F0] px-1 rounded">LPG&nbsp;ID</code>,{" "}
            <code className="bg-[#F5F5F0] px-1 rounded">DAC</code>,{" "}
            <code className="bg-[#F5F5F0] px-1 rounded">Staff</code>, … (extras
            ignored). <b>Bharat Gas / BPCL agencies only</b> — other rows will
            land in the Unmatched bucket. Duplicate LPG (same ID already
            resent by anyone) will show a{" "}
            <span className="text-red-600 font-semibold">
              red &quot;already resent&quot;
            </span>{" "}
            warning.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            data-testid="resend-bulk-textarea"
            placeholder={`ENGINEER BHARAT GAS\t100000001216216971\t4134758218\tsent jitendra\nSHIV SHAKTI BHARAT GAS\t100000000587338165\t4047922114\tsent jitendra`}
            className="w-full px-3 py-2 border border-[#DCDCD3] rounded font-mono text-xs focus:outline-none focus:border-[#385C4B] bg-white"
          />
          {err && (
            <div
              className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800"
              data-testid="resend-bulk-error"
            >
              {err}
            </div>
          )}
          {preview && (
            <div
              className="space-y-2 text-xs"
              data-testid="resend-bulk-preview"
            >
              <div className="font-mono tracking-[0.2em] uppercase text-[10px] text-[#6B6B61]">
                Preview · {preview.total_parsed} parsed
                {preview.already_count > 0 && (
                  <span className="ml-3 text-red-700">
                    · {preview.already_count} already resent (will skip)
                  </span>
                )}
              </div>
              <PreviewBucket
                title="Matched (Bharat Gas)"
                rows={preview.matched}
                tint="emerald"
                testid="bulk-bucket-matched"
              />
              {preview.fuzzy.length > 0 && (
                <>
                  <PreviewBucket
                    title={`Fuzzy (closest match) · ${preview.fuzzy.length}`}
                    rows={preview.fuzzy}
                    tint="amber"
                    testid="bulk-bucket-fuzzy"
                  />
                  <label className="flex items-center gap-2 text-[11px] font-mono text-amber-700">
                    <input
                      type="checkbox"
                      checked={includeFuzzy}
                      onChange={(e) => setIncludeFuzzy(e.target.checked)}
                      data-testid="resend-bulk-include-fuzzy"
                      className="accent-amber-600"
                    />
                    Include fuzzy matches in the commit (confirm spelling)
                  </label>
                </>
              )}
              {preview.unmatched.length > 0 && (
                <PreviewBucket
                  title={`Unmatched · ${preview.unmatched.length}`}
                  rows={preview.unmatched}
                  tint="red"
                  testid="bulk-bucket-unmatched"
                />
              )}
            </div>
          )}
          {result && (
            <div
              data-testid="resend-bulk-result"
              className="text-xs font-mono bg-emerald-50 border border-emerald-300 rounded p-3 space-y-1"
            >
              <div className="text-emerald-800 font-bold">
                ✓ Created: {(result.created || []).length}
              </div>
              {(result.skipped || []).length > 0 && (
                <div className="text-amber-700">
                  ⚠ Skipped: {result.skipped.length}
                </div>
              )}
              {(result.needs_confirm || []).length > 0 && (
                <div className="text-amber-700">
                  ⚠ Needed confirm (not committed): {result.needs_confirm.length}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#DCDCD3] flex justify-end gap-2 bg-white rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#DCDCD3] rounded hover:bg-[#F5F5F0]"
          >
            Close
          </button>
          <button
            onClick={doPreview}
            disabled={busy || !text.trim()}
            data-testid="resend-bulk-preview-btn"
            className="px-4 py-2 text-sm border border-[#385C4B] text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white disabled:opacity-50"
          >
            {busy ? "…" : "Preview"}
          </button>
          <button
            onClick={doCommit}
            disabled={
              busy ||
              !preview ||
              preview.matched.length + (includeFuzzy ? preview.fuzzy.length : 0) === 0
            }
            data-testid="resend-bulk-submit"
            className="px-5 py-2 text-sm bg-[#385C4B] text-white rounded hover:bg-[#2d4a3c] disabled:opacity-50 font-semibold"
          >
            {busy ? "Submitting…" : "Submit All"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewBucket({ title, rows, tint, testid }) {
  const palette = {
    emerald: { border: "border-emerald-300", bg: "bg-emerald-50", title: "text-emerald-800" },
    amber:   { border: "border-amber-300",   bg: "bg-amber-50",   title: "text-amber-800" },
    red:     { border: "border-red-300",     bg: "bg-red-50",     title: "text-red-800" },
  }[tint] || { border: "border-[#DCDCD3]", bg: "bg-white", title: "text-[#1C1C19]" };
  return (
    <div className={`border ${palette.border} rounded ${palette.bg} p-2`} data-testid={testid}>
      <div className={`font-mono text-[10px] uppercase tracking-[0.2em] mb-1 ${palette.title}`}>
        {title} ({rows.length})
      </div>
      <div className="space-y-0.5 max-h-40 overflow-y-auto">
        {rows.slice(0, 60).map((r, i) => (
          <div key={`${r.lpg_id}-${i}`} className="font-mono text-[10px] flex items-center gap-2">
            <span className="font-bold text-[#1C1C19] w-20 truncate">{r.lpg_id}</span>
            <span className="text-[#6B6B61]">→</span>
            <span className="flex-1 truncate">
              {r.agency_resolved || r.agency_typed}
            </span>
            {r.already_resent_by && (
              <span
                className="text-red-700 text-[9px] font-bold bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap"
                title={`Already resent by ${r.already_resent_by} on ${r.already_resent_at || ""}. Will be skipped on submit.`}
                data-testid={`resend-bulk-already-${i}`}
              >
                ⚠ ALREADY: {r.already_resent_by}
                {r.already_resent_at ? ` · ${r.already_resent_at.slice(0, 10)}` : ""}
              </span>
            )}
            {r.date && (
              <span
                className="text-[#385C4B] text-[9px] tabular-nums whitespace-nowrap"
                title="Agency-side date from paste"
              >
                {r.date}
              </span>
            )}
            {r.staff_name && (
              <span
                className={
                  r.staff_user_id
                    ? r.staff_exact
                      ? "text-emerald-700 text-[9px] font-semibold whitespace-nowrap"
                      : "text-amber-700 text-[9px] font-semibold whitespace-nowrap"
                    : "text-red-600 text-[9px] italic whitespace-nowrap"
                }
                title={
                  r.staff_warning ||
                  (r.staff_candidates && r.staff_candidates.length > 1
                    ? `picked "${r.staff_name}" out of [${r.staff_candidates.join(", ")}] — matched resend_staff user`
                    : r.staff_user_id
                    ? "matched to resend_staff user"
                    : "raw pasted name")
                }
              >
                {r.staff_name}
                {r.staff_user_id && r.staff_candidates && r.staff_candidates.length > 1 && (
                  <span
                    className="ml-1 text-emerald-500"
                    title={`Auto-picked from ${r.staff_candidates.length} candidates`}
                  >
                    ✓
                  </span>
                )}
                {r.staff_user_id && !r.staff_exact && r.staff_raw && r.staff_raw !== r.staff_name && (
                  <span className="ml-1 text-amber-600">≈</span>
                )}
                {!r.staff_user_id && r.staff_raw && (
                  <span
                    className="ml-1 text-red-500"
                    title="No resend_staff match — assign manually before committing"
                  >
                    ⚠
                  </span>
                )}
              </span>
            )}
            {r.warning && (
              <span className="text-red-600 text-[9px] italic truncate max-w-[160px]" title={r.warning}>
                {r.warning}
              </span>
            )}
          </div>
        ))}
        {rows.length > 60 && (
          <div className="text-[9px] text-[#6B6B61] italic mt-1">
            …and {rows.length - 60} more
          </div>
        )}
      </div>
    </div>
  );
}

function stripPreview(r) {
  return {
    agency_name: r.agency_typed,
    lpg_id: r.lpg_id,
    dac: r.dac || "",
    mobile: r.mobile || "",
    reason: r.reason || "",
    staff_name: r.staff_name || "",
    staff_candidates: r.staff_candidates || [],
    date: r.date || "",
    // Passing this through so the commit endpoint's "skip if already
    // resent" contract has all the info it needs even if a race put a
    // new row in between preview and commit.
    already_resent_by: r.already_resent_by || "",
  };
}

// -----------------------------------------------------------------------------
// CSV helpers — kept dependency-free so the modal loads instantly.
// -----------------------------------------------------------------------------

/**
 * Split a CSV blob into rows, honouring quoted fields (including the
 * `="123..."` Excel-safe format the Bharat booking history uses to keep
 * long consumer numbers from being auto-formatted as scientific).
 * Returns an array of rows; each row is a tab-joined string so the same
 * downstream parser used for pasted text can consume it.
 */
function _parseCsvLines(csvText) {
  const rows = [];
  let row = [];
  let cur = "";
  let i = 0;
  let inQ = false;
  const s = csvText.replace(/\r\n?/g, "\n");
  while (i < s.length) {
    const c = s[i];
    if (inQ) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQ = false;
        i += 1;
        continue;
      }
      cur += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQ = true;
      i += 1;
      continue;
    }
    if (c === ",") {
      row.push(_normaliseCell(cur));
      cur = "";
      i += 1;
      continue;
    }
    if (c === "\n") {
      row.push(_normaliseCell(cur));
      rows.push(row.join("\t"));
      row = [];
      cur = "";
      i += 1;
      continue;
    }
    cur += c;
    i += 1;
  }
  if (cur.length || row.length) {
    row.push(_normaliseCell(cur));
    rows.push(row.join("\t"));
  }
  return rows;
}

function _normaliseCell(cell) {
  // `="123..."` → `123...`
  let v = String(cell ?? "").trim();
  if (v.startsWith('=')) v = v.slice(1);
  if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
  return v;
}

/**
 * Peek at the header row of a spreadsheet dump and rebuild every data row
 * into the exact tab-separated layout the server-side parser accepts:
 *
 *     Agency<TAB>LPG_ID<TAB>DAC<TAB>Staff
 *
 * Extra columns (Mobile, Status, When, Message …) are ignored. If we
 * can't detect the header (a manually-typed paste for example), we return
 * the input unchanged so the paste-first workflow still works.
 */
function _rebuildRowsForParser(rawLines) {
  if (!rawLines.length) return "";
  const header = rawLines[0].toLowerCase();
  const hdrCells = header.split("\t").map((s) => s.trim());
  const iAgency = hdrCells.findIndex((h) => /agenc/.test(h));
  const iLpg = hdrCells.findIndex(
    (h) => /consumer|consumer\s*no|lpg\s*id|lpg/.test(h)
  );
  const iDac = hdrCells.findIndex((h) => /^dac$|dac[^_a-z]/.test(h) && !/status/.test(h));
  const iStaff = hdrCells.findIndex((h) => /staff|team|by\s*name/.test(h));

  // If we can't find EITHER an agency AND an lpg column, treat the input
  // as an already-tab-separated paste — let the server-side parser deal
  // with it directly.
  if (iAgency < 0 || iLpg < 0) {
    return rawLines.join("\n");
  }

  const outLines = [];
  for (let r = 1; r < rawLines.length; r++) {
    const cells = rawLines[r].split("\t");
    const agency = (cells[iAgency] || "").trim();
    const lpg = (cells[iLpg] || "").trim();
    if (!agency || !lpg) continue;
    const dac = iDac >= 0 ? (cells[iDac] || "").trim() : "";
    const staff = iStaff >= 0 ? (cells[iStaff] || "").trim() : "";
    outLines.push([agency, lpg, dac, staff].filter(Boolean).join("\t"));
  }
  return outLines.join("\n");
}
